import moment from 'moment';
import momentTz from 'moment-timezone';
import _ from 'lodash';

import { DateDueKey } from 'global-services/register/DateDueModels';

export const dateDiffOnly = (date: string) => {
  const now = moment(new Date()).startOf('day');
  const due = moment(new Date(date)).startOf('day');
  return moment(due).diff(now, 'days');
};

export const dateDiffBetween = (date1: string, date2: string) => {
  const now = moment(new Date(date1)).startOf('day');
  const due = moment(new Date(date2)).startOf('day');
  return moment(due).diff(now, 'days');
};

export const getTodayDateString = () => {
  const d = new Date();
  let month = `${d.getMonth() + 1}`;
  let day = `${d.getDate()}`;
  const year = d.getFullYear();

  if (month.length < 2) month = `0${month}`;
  if (day.length < 2) day = `0${day}`;

  return [year, month, day].join('-');
};

export const getCurrentTime = () => {
  const currentTime = moment();

  const formattedTime = {
    hours: currentTime.hours(),
    minutes: currentTime.minutes(),
  };

  return formattedTime;
};

export const isValidDate = (dateString: string) => {
  const regEx = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateString.match(regEx)) return false; // Invalid format
  const d = new Date(dateString);
  const dNum = d.getTime();
  if (!dNum && dNum !== 0) return false; // NaN value, Invalid date
  return d.toISOString().slice(0, 10) === dateString;
};

export const toDate = (input: string) => {
  if (input === undefined) return moment();
  const date = new Date(input);
  if (date.toString() === 'Invalid Date') {
    return input;
  }
  return moment(date).format('YYYY-MM-DD');
};

export const toLocalTime = (utcString: string) => {
  if (utcString) {
    const time = moment(new Date(utcString));
    return time.format('YYYY-MM-DD HH:mm:ss');
  }
  return '';
};

export const compareStrings = (
  options: Array<'ignoreInBetweenSpace' | 'ignoreWhitespace' | 'ignoreCase'>,
  arg1: string,
  arg2: string
) => {
  if (!_.isString(arg1) || !_.isString(arg2)) {
    return false;
  }

  let a = _.cloneDeep(arg1);
  let b = _.cloneDeep(arg2);

  if (options && _.includes(options, 'ignoreInBetweenSpace')) {
    b = b.replace(/\s{2,}/g, ' ');
  }

  if (options && _.includes(options, 'ignoreWhitespace')) {
    a = a.replace(/\s/g, '');
    b = b.replace(/\s/g, '');
  }

  if (options && _.includes(options, 'ignoreCase')) {
    a = a.toLowerCase();
    b = b.toLowerCase();
  }

  return a === b;
};

export const formatDateTime = (time: string) => moment(time).format('YYYY-MM-DD hh:mm a');

const formatOffset = (offsetMinutes: number) => {
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absMinutes = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absMinutes / 60)).padStart(2, '0');
  const minutes = String(absMinutes % 60).padStart(2, '0');
  return `GMT${sign}${hours}:${minutes}`;
};

export const dynamicTimezoneList = () => {
  const allTimezones = momentTz.tz
    .names()
    ?.filter((tz) => !tz.startsWith('Etc/'))
    ?.map((tz) => {
      const offset = momentTz.tz(tz).utcOffset();
      const gmt = formatOffset(offset);

      const parts = tz.split('/');
      const city = parts[parts.length - 1].replace(/_/g, ' ');

      return {
        value: tz,
        name: `${gmt} - ${city}`,
        offset,
      };
    });

  return allTimezones
    .sort((a, b) => b.offset - a.offset)
    .map(({ value, name }) => ({ value, name }));
};

export const getHourMinuteFrom12HrsTime = (timeStr: string): [number, number] => {
  const [hour, minute] = timeStr
    ? [moment(timeStr, 'hh:mm A').hours(), moment(timeStr, 'hh:mm A').minutes()]
    : [0, 0];
  return [hour, minute];
};
export const convert24To12HourFormat = (timeStr: string): string => {
  return moment(timeStr, 'HH:mm').format('hh:mm A');
};

export const defaultScheduleTimeOptions = [...Array(24)]
  .map((__, hour) => [
    {
      value: moment().set({ hours: hour, minutes: 0 }).format('hh:mm A'),
      name: moment().set({ hours: hour, minutes: 0 }).format('hh:mm A'),
    },
    {
      value: moment().set({ hours: hour, minutes: 30 }).format('hh:mm A'),
      name: moment().set({ hours: hour, minutes: 30 }).format('hh:mm A'),
    },
  ])
  .flat();

export const returnManualDueDateFilter = (value: string | { start: string; end: string }) => {
  // if value is one of string options
  if (typeof value === 'string') {
    const currentDate = moment().startOf('day').format('YYYY-MM-DD');

    const getEndDate = () => {
      switch (value) {
        case DateDueKey.HideOverdue:
          return moment(currentDate).add(1000, 'months').format('YYYY-MM-DD');
        case DateDueKey.DueToday:
          return moment().endOf('day').format('YYYY-MM-DD');
        case DateDueKey.ApproachingDueDate:
          return moment(currentDate).add(14, 'days').format('YYYY-MM-DD');
        default:
          return '';
      }
    };

    const startDate =
      value === DateDueKey.ApproachingDueDate
        ? moment(currentDate).add(1, 'days').format('YYYY-MM-DD')
        : currentDate;

    return {
      start: startDate,
      end: getEndDate(),
    };
  }

  // custom date
  return value;
};
